import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Maximize2, MessageCircle, X } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Badge } from '../ui/badge.tsx';
import { Card } from '../ui/card.tsx';
import userTeamService from '../../services/userTeamService';
import {
  subscribeToAllSupportChats,
  subscribeToSupportChatThreadDoc,
  supportChatThreadId,
} from '../../services/supportChatService';
import ChatPanel from './ChatPanel';
import AdminChatInbox from './AdminChatInbox';
import ChatModal from './ChatModal';
import { useMediaQuery } from '../../hooks/use-mobile.tsx';
import { onOpenSupportChatRequest, publishSupportChatUnread } from '../../services/supportChatBus';

/**
 * Persistent bottom-right chat widget. Regular users get a single thread with
 * the Advicelink team; super admins get an inbox of all user conversations.
 * Expandable into a focused modal view. `callSlot` (the call window) sits to
 * the left of the chat button and, unlike the chat, also floats on phones.
 * `paraSlot` (the floating Para button, Oct 2026) sits between them.
 */
const SupportChatWidget = ({ user, enabled = true, callSlot = null, paraSlot = null }) => {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const userEmail = supportChatThreadId(user?.email);
  const isAdmin = Boolean(
    enabled && userEmail && userTeamService.getUserRole(userEmail) === 'super_admin'
  );

  // User mode: own thread doc (unread badge). Admin mode: all threads.
  const [ownThread, setOwnThread] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThreadEmail, setSelectedThreadEmail] = useState(null);

  useEffect(() => {
    if (!enabled || !userEmail || isAdmin) return undefined;
    return subscribeToSupportChatThreadDoc(userEmail, setOwnThread);
  }, [enabled, userEmail, isAdmin]);

  useEffect(() => {
    if (!enabled || !isAdmin) return undefined;
    return subscribeToAllSupportChats(setThreads);
  }, [enabled, isAdmin]);

  const totalUnread = useMemo(() => {
    if (isAdmin) {
      return threads.reduce((sum, t) => sum + (Number(t.unreadForAdmin) || 0), 0);
    }
    return Number(ownThread?.unreadForUser) || 0;
  }, [isAdmin, threads, ownThread]);

  const selectedThread = isAdmin
    ? threads.find((t) => t.id === selectedThreadEmail) || null
    : null;

  // Phones: no floating button (it covered footers and pagination). The header's
  // overflow menu opens the chat full-screen instead.
  const isPhone = useMediaQuery('(max-width: 639px)');
  useEffect(() => publishSupportChatUnread(enabled ? totalUnread : 0), [enabled, totalUnread]);
  useEffect(() => onOpenSupportChatRequest(() => {
    setOpen(false);
    setModalOpen(true);
  }), []);

  const chatEnabled = Boolean(enabled && userEmail);
  if (!chatEnabled && !callSlot) return null;

  const openExpanded = () => {
    setOpen(false);
    setModalOpen(true);
  };

  const collapseToWidget = () => {
    setModalOpen(false);
    setOpen(true);
  };

  const panelActive = open && !modalOpen;

  return (
    <>
      <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-3">
        {chatEnabled && panelActive && (
          <Card className="max-sm:hidden w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-8rem)] flex flex-col overflow-hidden p-0 shadow-lg">
            <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border">
              {isAdmin && selectedThreadEmail ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => setSelectedThreadEmail(null)}
                  aria-label="Back to conversations"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              ) : (
                <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">
                  {isAdmin
                    ? selectedThreadEmail
                      ? selectedThread?.userName
                        || userTeamService.getUserName(selectedThreadEmail)
                        || selectedThreadEmail
                      : 'Support chats'
                    : 'Support chat'}
                </p>
                <p className="text-[11px] text-muted-foreground leading-tight truncate">
                  {isAdmin
                    ? selectedThreadEmail || 'Conversations with users'
                    : "We'll get back to you as soon as possible"}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={openExpanded}
                aria-label="Expand chat"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {isAdmin ? (
              selectedThreadEmail ? (
                <ChatPanel
                  key={selectedThreadEmail}
                  threadEmail={selectedThreadEmail}
                  viewer="admin"
                  user={user}
                  active={panelActive}
                  unreadCount={Number(selectedThread?.unreadForAdmin) || 0}
                />
              ) : (
                <AdminChatInbox
                  threads={threads}
                  selectedThreadEmail={selectedThreadEmail}
                  onSelectThread={setSelectedThreadEmail}
                  currentUserEmail={userEmail}
                />
              )
            ) : (
              <ChatPanel
                threadEmail={userEmail}
                viewer="user"
                user={user}
                active={panelActive}
                unreadCount={totalUnread}
              />
            )}
          </Card>
        )}

        <div className="flex items-end gap-3">
        {callSlot}
        {paraSlot}
        {chatEnabled && (
        <div className="relative max-sm:hidden">
          <Button
            type="button"
            size="icon"
            className="h-12 w-12 rounded-full shadow-lg"
            onClick={() => setOpen((prev) => !prev)}
            aria-label={open ? 'Close support chat' : 'Open support chat'}
          >
            {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
          </Button>
          {totalUnread > 0 && !open && !modalOpen && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 justify-center pointer-events-none"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          )}
        </div>
        )}
        </div>
      </div>

      {chatEnabled && (
      <ChatModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCollapse={isPhone ? undefined : collapseToWidget}
        viewer={isAdmin ? 'admin' : 'user'}
        user={user}
        threadEmail={userEmail}
        unreadCount={totalUnread}
        threads={threads}
        selectedThreadEmail={selectedThreadEmail}
        onSelectThread={setSelectedThreadEmail}
      />
      )}
    </>
  );
};

export default SupportChatWidget;
